import React, { memo } from "react";

import { Box, Span } from "@chakra-ui/react";
import { ReactComponent as ArrowRightIcon } from "assets/icons/arrow-right.svg";
import { ReactComponent as CloseIcon } from "assets/icons/close-icon-in-grey-bg.svg";
import { ReactComponent as ExportIcon } from "assets/icons/table-export-icon.svg";

import DateInput from "ui/date-input";
import { DropDown } from "ui/dropdown";
import { spinnerIcon } from "ui/filter/filter-footer";
import {
  FilterFooterBtn,
  applyBtnCss,
  cancelBtnCss,
  filterFooterBtnCss,
  loadingBtnCss,
} from "ui/filter/filter-footer/styles";
import { Heading4, Paragraph3, Paragraph4 } from "ui/typography";

import {
  exportBtnCss,
  exportDropDownCss,
  radioCircle,
  radioWrapper,
} from "./styles";
import { IExportProps } from "./types";

const DROP_DOWN_WIDTH = 384;
export const Export = memo((props: IExportProps) => {
  const {
    isLoading,
    handleExport,
    dateRange,
    exportType,
    handleExportTypeChange,
    handleDateRangeChange,
    dropDownRef,
    availableExportOptions,
    handleResetExport,
  } = props;

  const handleToggleExport = () => {
    if (!dropDownRef?.current) return;
    dropDownRef.current.handleToggleDropdown();
  };

  return (
    <>
      <DropDown
        dropDownWidth={DROP_DOWN_WIDTH}
        dropDownBtnContent={
          <>
            <ExportIcon className="w-4 h-4" />
            <p className="text-sm text-[#374151] font-semibold">Export</p>
          </>
        }
        customDropDownButtonCss={exportBtnCss}
        dropDownPopUpMenuContentCss={exportDropDownCss}
        ref={dropDownRef}
        allowOutSideClick
      >
        <>
          <Box className="h-[46px] flex items-center justify-between border-b border-[#F2F4F7] pt-[10px] px-4">
            <Heading4
              lineHeight="22px"
              textColor="#101828"
              fontWeight="600"
            >
              Export
            </Heading4>
            <Box
              className={`${isLoading ? "cursor-not-allowed" : "cursor-pointer"}`}
              onClick={!isLoading ? handleToggleExport : undefined}
            >
              <CloseIcon />
            </Box>
          </Box>
          <Box className="h-[86px] border-b border-[#F2F4F7] px-4">
            <Box className="h-10 flex items-center">
              <Paragraph4
                textColor="#344054"
                fontWeight="500"
                lineHeight="20px"
              >
                Date Range
              </Paragraph4>
            </Box>
            <Box className="flex items-center justify-between gap-[13px] h-9">
              <Box className="inline-flex items-center h-9 w-[152px]">
                <DateInput
                  name="startDate"
                  value={dateRange.startDate}
                  onChange={(value) => {
                    if (!value) return;
                    // Prevent selecting a startDate that is after endDate
                    if (
                      dateRange.endDate &&
                      value > new Date(dateRange.endDate)
                    ) {
                      return;
                    }
                    handleDateRangeChange("startDate", value);
                  }}
                  placeholder="Start Date"
                  preventOpenOnFocus
                />
              </Box>
              <Box>
                <ArrowRightIcon />
              </Box>
              <Box className="inline-flex items-center h-9 w-[152px]">
                <DateInput
                  name="endDate"
                  value={dateRange.endDate}
                  onChange={(value) => {
                    if (!value) return;
                    // Prevent selecting an endDate that is before startDate
                    if (
                      dateRange.startDate &&
                      value < new Date(dateRange.startDate)
                    ) {
                      return;
                    }
                    handleDateRangeChange("endDate", value);
                  }}
                  placeholder="End Date"
                  preventOpenOnFocus
                />
              </Box>
            </Box>
          </Box>
          <Box className="h-20 px-4 border-t border-[#F2F4F7]">
            <Box className="h-10 flex items-center">
              <Paragraph4
                textColor="#1A202C"
                fontWeight="500"
                lineHeight="20px"
              >
                Export as
              </Paragraph4>
            </Box>
            <Box className="h-10 flex items-center gap-4">
              {availableExportOptions.map((exportOption, idx) => (
                <Box
                  css={radioWrapper}
                  className="w-[66px] flex items-center gap-2"
                  key={idx}
                >
                  <label className="flex items-center cursor-pointer text-[16px] text-[#4a4a4a]">
                    <input
                      type="radio"
                      name="exportType"
                      value={exportOption}
                      onChange={handleExportTypeChange}
                      checked={exportType === exportOption}
                    />
                    <Span
                      className="custom-radio-circle"
                      css={radioCircle}
                    ></Span>
                    <Paragraph3
                      fontWeight="500"
                      lineHeight="20px"
                      textColor="#344054"
                      className="uppercase"
                    >
                      {exportOption}
                    </Paragraph3>
                  </label>
                </Box>
              ))}
            </Box>
          </Box>
          <Box className="h-[78px] pt-6 px-4 w-full">
            <Box className="h-[44px] flex justify-end gap-3">
              <FilterFooterBtn
                onClick={!isLoading ? handleResetExport : undefined}
                css={{ ...filterFooterBtnCss, ...cancelBtnCss }}
                disabled={isLoading}
              >
                Reset
              </FilterFooterBtn>
              <FilterFooterBtn
                onClick={!isLoading ? handleExport : undefined}
                css={{
                  ...filterFooterBtnCss,
                  ...applyBtnCss,
                  ...(isLoading && loadingBtnCss),
                }}
                disabled={
                  isLoading ||
                  !dateRange.startDate ||
                  !dateRange.endDate ||
                  !exportType
                }
              >
                {isLoading ? (
                  <Box
                    display="flex"
                    alignItems="center"
                    gap={2}
                  >
                    <span>{spinnerIcon}</span>
                    <span>Loading</span>
                  </Box>
                ) : (
                  "Export"
                )}
              </FilterFooterBtn>
            </Box>
          </Box>
        </>
      </DropDown>
    </>
  );
});
